/**
 * Moteur d'adaptation des quantités (§3.1 CLAUDE.md).
 *
 * Facteur = portions_cible / recipe.servings. Chaque export ci-dessous
 * correspond à une ligne du tableau du §3.1 ; voir les tests dans
 * server/test/ingredients/adaptQuantities.test.ts pour la correspondance.
 *
 * Ce module ne touche jamais aux étapes (temps, températures, texte) : elles
 * sont renvoyées telles quelles par `adaptRecipeQuantities`, jamais mises à
 * l'échelle, jamais réécrites (§3.1 : « Temps », « Températures », « Texte des
 * étapes »).
 */

import type { Scaling, Unit } from '@mlc/shared';
// Import depuis '../AppError' et non '../errors' : ce module doit rester
// importable par le client (US-07, même principe que parseIngredientLine
// pour US-01) sans traîner errors.ts -> config.ts -> node:fs dans le bundle.
import { AppError } from '../AppError';
import { FRACTION_DISPLAY_UNITS, isCountableUnit } from './units';

// ----------------------------------------------------------------------------
// Facteur
// ----------------------------------------------------------------------------

/** Un nombre de portions exploitable : présent, fini, strictement positif. */
function isUsableServings(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

/**
 * `recipe.servings` est REAL nullable : une recette peut ne pas porter de
 * nombre de portions. Sans garde, le facteur vaut NaN ou Infinity, aucun
 * avertissement ne se déclenche et l'affichage sort la chaîne « NaN » — faux et
 * silencieux, exactement ce que le §3 cherche à éviter. On refuse d'adapter.
 */
export function computeFactor(referenceServings: number | null, targetServings: number | null): number {
  if (!isUsableServings(referenceServings) || !isUsableServings(targetServings)) {
    throw new AppError(
      400,
      'portions_invalides',
      'Nombre de portions manquant ou invalide : impossible d\'adapter les quantités.',
    );
  }
  return targetServings / referenceServings;
}

/** Bandeau non bloquant au-delà de ×2 ou en dessous de ×0,5 (§3.1). */
export function isSignificantAdaptation(factor: number): boolean {
  return factor > 2 || factor < 0.5;
}

// ----------------------------------------------------------------------------
// Modes de mise à l'échelle
// ----------------------------------------------------------------------------

/**
 * Applique le mode de mise à l'échelle d'un ingrédient à une quantité.
 *   - linear (défaut) : quantity × facteur.
 *   - none             : quantité inchangée (eau de cuisson, huile de friture…).
 *   - sublinear        : quantity × facteur^0,8 (sel, épices, levure…).
 */
export function scaleByMode(quantity: number, factor: number, scaling: Scaling): number {
  switch (scaling) {
    case 'none':
      return quantity;
    case 'sublinear':
      return quantity * factor ** 0.8;
    case 'linear':
      return quantity * factor;
  }
}

/** Ingrédients dénombrables (œufs, gousses d'ail, feuilles de laurier…) : demi le plus proche. */
export function roundToNearestHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

const EGG_PATTERN = /\boeufs?\b/;

/**
 * Détecte un ingrédient « œuf », indépendamment de l'accentuation ou de la
 * ligature (« œuf »/« oeuf »), pour la mention du demi-œuf (§3.1).
 * Ne dépend pas de `name_normalized` (calculé ailleurs) : la fonction
 * normalise elle-même le nom qu'on lui passe.
 */
export function isEggName(name: string): boolean {
  const normalized = name
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .normalize('NFD')
    .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
  return EGG_PATTERN.test(normalized);
}

// ----------------------------------------------------------------------------
// Arrondi d'affichage
// ----------------------------------------------------------------------------

/**
 * Arrondi « deux chiffres significatifs maximum » (§3.1). Le brief donne
 * l'exemple 333,333 g -> 335 g, qui ne correspond pas à un arrondi
 * mathématique strict à 2 chiffres significatifs (330, au multiple de 10 le
 * plus proche à cette échelle). On arrondit donc au multiple de 5 le plus
 * proche à l'échelle des 2 premiers chiffres significatifs : c'est la
 * précision réaliste d'une balance ou d'un verre doseur de cuisine, et c'est
 * ce qui reproduit exactement l'exemple du brief.
 */
export function roundToTwoSignificantDigits(value: number): number {
  if (value === 0) return 0;
  const sign = Math.sign(value);
  const abs = Math.abs(value);
  const magnitude = Math.floor(Math.log10(abs));
  const step = 5 * 10 ** (magnitude - 2);
  const rounded = Math.round(abs / step) * step;
  // Nettoie le bruit de virgule flottante (ex. 0.30000000000000004).
  return sign * Number(rounded.toFixed(10));
}

function formatPlainNumber(value: number): string {
  return Number(value.toFixed(6)).toString();
}

const COMMON_FRACTIONS: ReadonlyArray<{ frac: number; glyph: string }> = [
  { frac: 1 / 4, glyph: '¼' },
  { frac: 1 / 3, glyph: '⅓' },
  { frac: 1 / 2, glyph: '½' },
  { frac: 2 / 3, glyph: '⅔' },
  { frac: 3 / 4, glyph: '¾' },
];
const FRACTION_TOLERANCE = 0.01;

function toFractionDisplay(value: number): { quantity: number; display: string } | null {
  const whole = Math.trunc(value);
  const remainder = Math.abs(value - whole);

  if (remainder < FRACTION_TOLERANCE) {
    return whole === 0 ? null : { quantity: whole, display: String(whole) };
  }

  const match = COMMON_FRACTIONS.find((candidate) => Math.abs(remainder - candidate.frac) < FRACTION_TOLERANCE);
  if (!match) return null;

  const quantity = whole + match.frac;
  const display = whole === 0 ? match.glyph : `${whole} ${match.glyph}`;
  return { quantity, display };
}

/**
 * Arrondi d'affichage d'une quantité déjà adaptée (§3.1). Les cuillères et les
 * mesures anglo-saxonnes de volume (c. à c., c. à s., cup, tsp, tbsp)
 * privilégient une fraction usuelle ; les autres unités (masse, dénombré,
 * grands volumes) affichent un nombre arrondi à 2 chiffres significatifs.
 */
export function formatQuantityForDisplay(value: number, unit: Unit | null): { quantity: number; display: string } {
  const preferFraction = unit !== null && (FRACTION_DISPLAY_UNITS.has(unit) || isCountableUnit(unit));
  if (preferFraction) {
    const fraction = toFractionDisplay(value);
    if (fraction) return fraction;
  }

  const rounded = roundToTwoSignificantDigits(value);
  return { quantity: rounded, display: formatPlainNumber(rounded) };
}

// ----------------------------------------------------------------------------
// Adaptation d'un ingrédient
// ----------------------------------------------------------------------------

export interface QuantityInput {
  quantity: number | null;
  unit: Unit | null;
  scaling: Scaling;
  /** Nom affiché de l'ingrédient, utilisé uniquement pour détecter les œufs. */
  name: string;
}

export interface AdaptedQuantity {
  quantity: number | null;
  unit: Unit | null;
  /** Chaîne prête à afficher (fraction ou nombre arrondi), ou null si `quantity` est null. */
  display: string | null;
  /** Mention complémentaire (ex. mise en garde du demi-œuf), sinon null. */
  note: string | null;
}

export function adaptIngredientQuantity(input: QuantityInput, factor: number): AdaptedQuantity {
  if (input.quantity === null) {
    // « sel, poivre » n'a pas de quantité : rien à mettre à l'échelle.
    return { quantity: null, unit: input.unit, display: null, note: null };
  }

  const scaled = scaleByMode(input.quantity, factor, input.scaling);
  const countable = input.unit !== null && isCountableUnit(input.unit);
  const preRounded = countable ? roundToNearestHalf(scaled) : scaled;

  let note: string | null = null;
  if (countable && isEggName(input.name) && !Number.isInteger(preRounded)) {
    note = '½ œuf ≈ 25 g, à peser';
  }

  const { quantity, display } = formatQuantityForDisplay(preRounded, input.unit);
  return { quantity, unit: input.unit, display, note };
}

// ----------------------------------------------------------------------------
// Adaptation d'une recette entière
// ----------------------------------------------------------------------------

/** Sous-ensemble d'une étape pertinent pour vérifier qu'elle n'est jamais mise à l'échelle. */
export interface StepLike {
  duration_sec: number | null;
  temperature_c: number | null;
  instruction: string;
}

export interface AdaptRecipeInput {
  /** `recipe.servings`, nullable en base : le facteur est refusé s'il manque. */
  referenceServings: number | null;
  targetServings: number | null;
  ingredients: readonly QuantityInput[];
  steps: readonly StepLike[];
}

export interface AdaptRecipeResult {
  factor: number;
  /** Bandeau non bloquant à afficher si le facteur dépasse ×2 ou passe sous ×0,5. */
  warning: boolean;
  ingredients: AdaptedQuantity[];
  /**
   * Renvoyées telles quelles (même référence) : les temps, les températures et
   * le texte des étapes ne sont jamais recalculés ni réécrits par
   * l'adaptation des quantités (§3.1).
   */
  steps: readonly StepLike[];
}

export function adaptRecipeQuantities(input: AdaptRecipeInput): AdaptRecipeResult {
  const factor = computeFactor(input.referenceServings, input.targetServings);
  return {
    factor,
    warning: isSignificantAdaptation(factor),
    ingredients: input.ingredients.map((ingredient) => adaptIngredientQuantity(ingredient, factor)),
    steps: input.steps,
  };
}
